import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useRequireApproved } from '../lib/auth';
import {
  useChecklist,
  useChecklistItemStates,
  useToggleChecklistItem,
  useWorkspace,
  type ItemState,
} from '../lib/queries';
import {
  CATEGORIES,
  CHECKLIST,
  PATHWAYS,
  SCENARIOS,
  SUB_PATHWAYS_C,
} from '../data/checklist';
import {
  bucketByCategory,
  countItems,
  filterChecklist,
} from '../data/filter';
import type {
  Category,
  ChecklistCategory,
  Scenario,
  Selection,
} from '../data/types';

export function ChecklistEditPage() {
  const { id: orgId, cid } = useParams<{ id: string; cid: string }>();
  const auth = useRequireApproved();
  const workspaceQ = useWorkspace(orgId);
  const checklistQ = useChecklist(cid);
  const itemStatesQ = useChecklistItemStates(cid);
  const toggle = useToggleChecklistItem(cid);

  const [activeTab, setActiveTab] = useState<Category>('onboarding');
  /**
   * recentlySaved: item_id -> timestamp of when its toggle was confirmed.
   * Drives the brief "Saved ✓" hint after a successful upsert. Cleared
   * after 1.8s so the page settles back to its quiet state.
   */
  const [recentlySaved, setRecentlySaved] = useState<Record<string, number>>({});
  const [toggleErr, setToggleErr] = useState<string | null>(null);

  // Sweep stale "Saved" hints every ~600ms so they fade reliably.
  useEffect(() => {
    if (Object.keys(recentlySaved).length === 0) return;
    const t = setInterval(() => {
      const cutoff = Date.now() - 1800;
      setRecentlySaved((cur) => {
        let changed = false;
        const next: Record<string, number> = {};
        for (const [k, v] of Object.entries(cur)) {
          if (v >= cutoff) next[k] = v;
          else changed = true;
        }
        return changed ? next : cur;
      });
    }, 600);
    return () => clearInterval(t);
  }, [recentlySaved]);

  function doToggle(itemId: string, nextChecked: boolean) {
    setToggleErr(null);
    toggle.mutate(
      { item_id: itemId, checked: nextChecked },
      {
        onSuccess: () => {
          setRecentlySaved((cur) => ({ ...cur, [itemId]: Date.now() }));
        },
        onError: (e) => {
          setToggleErr(e instanceof Error ? e.message : String(e));
        },
      },
    );
  }

  const selection: Selection | null = useMemo(() => {
    const c = checklistQ.data;
    if (!c || !c.pathway) return null;
    return {
      pathway: c.pathway,
      subPathwayC: c.sub_pathway_c,
      scenarios: (c.scenarios ?? []) as Scenario[],
    };
  }, [checklistQ.data]);

  const filtered = useMemo(
    () => (selection ? filterChecklist(CHECKLIST, selection) : []),
    [selection],
  );
  const buckets = useMemo(() => bucketByCategory(filtered), [filtered]);
  const states = itemStatesQ.data ?? {};

  // Per-tab and overall progress
  const progress = useMemo(() => {
    const byCat: Record<Category, { done: number; total: number }> = {
      onboarding: { done: 0, total: 0 },
      optional: { done: 0, total: 0 },
      operational: { done: 0, total: 0 },
    };
    for (const cat of filtered) {
      for (const sec of cat.sections) {
        for (const it of sec.items) {
          byCat[cat.category].total += 1;
          if (states[it.id]?.checked) byCat[cat.category].done += 1;
        }
      }
    }
    const total =
      byCat.onboarding.total + byCat.optional.total + byCat.operational.total;
    const done =
      byCat.onboarding.done + byCat.optional.done + byCat.operational.done;
    return { byCat, total, done };
  }, [filtered, states]);

  if (auth.loading) return <CenterText>Loading…</CenterText>;
  if (!auth.user) return null;
  if (checklistQ.isLoading) return <CenterText>Loading checklist…</CenterText>;
  if (checklistQ.error) {
    return (
      <CenterText>
        <span className="text-[#a02525]">{(checklistQ.error as Error).message}</span>
      </CenterText>
    );
  }
  if (!checklistQ.data) return <CenterText>Checklist not found.</CenterText>;
  if (!selection) {
    return <CenterText>This checklist has no pathway set — it can't be opened.</CenterText>;
  }

  const c = checklistQ.data;
  const pathwayMeta = PATHWAYS.find((p) => p.id === selection.pathway)!;
  const subPathwayMeta =
    selection.pathway === 'C' && selection.subPathwayC
      ? SUB_PATHWAYS_C.find((sp) => sp.id === selection.subPathwayC)
      : null;
  const scenarioMetas = selection.scenarios
    .map((id) => SCENARIOS.find((s) => s.id === id))
    .filter((s): s is NonNullable<typeof s> => !!s);

  const activeMeta = CATEGORIES.find((cat) => cat.id === activeTab)!;
  const activeCats = buckets[activeTab];
  const overallPct =
    progress.total === 0 ? 0 : Math.round((progress.done / progress.total) * 100);

  return (
    <div className="max-w-4xl mx-auto px-6 py-10 md:py-12">
      {/* Crumb + title */}
      <Link
        to={`/workspace/${orgId}`}
        className="font-label text-[11px] uppercase tracking-[0.15em] text-ink-muted hover:text-accent transition-colors inline-block mb-6"
      >
        ← {workspaceQ.data?.name ?? 'workspace'}
      </Link>

      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-2">
        <div className="eyebrow">Checklist</div>
        <div className="font-label text-[10px] uppercase tracking-[0.18em] text-ink-muted tabular-nums">
          {progress.done} of {progress.total} done · {overallPct}%
        </div>
      </div>
      <h1 className="editorial-h1 mb-4">{c.name}</h1>

      {/* Overall progress bar */}
      <div className="h-1 bg-rule mb-6">
        <div
          className="h-full transition-all"
          style={{ width: `${overallPct}%`, background: 'var(--accent)' }}
        />
      </div>

      {/* Selection summary */}
      <div className="flex flex-wrap gap-2 mb-8">
        <Badge label={`Pathway ${pathwayMeta.id} — ${pathwayMeta.short}`} />
        {subPathwayMeta && <Badge label={`Sub-pathway ${subPathwayMeta.id}`} />}
        {scenarioMetas.map((s) => (
          <Badge key={s.id} label={`${s.id} — ${s.short}`} />
        ))}
      </div>

      {/* Toggle error banner — only shown when a save actually fails. */}
      {toggleErr && (
        <div className="border-l-4 border-[#a02525] bg-[#fbeaea] px-5 py-3 mb-6 flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <div className="font-label text-[10px] font-semibold uppercase tracking-[0.22em] text-[#a02525] mb-1">
              Couldn't save
            </div>
            <div className="font-editorial text-[14px] text-[#7a1c1c]">
              {toggleErr}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setToggleErr(null)}
            className="font-label text-[10px] uppercase tracking-[0.18em] text-[#7a1c1c] hover:text-[#a02525]"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Three-tab nav with per-tab progress */}
      <div className="grid grid-cols-3 gap-px bg-rule border border-rule mb-px">
        {CATEGORIES.map((cat) => {
          const isActive = cat.id === activeTab;
          const p = progress.byCat[cat.id];
          const pct = p.total === 0 ? 0 : Math.round((p.done / p.total) * 100);
          return (
            <button
              key={cat.id}
              type="button"
              onClick={() => setActiveTab(cat.id)}
              className="px-4 py-3 text-left bg-paper hover:bg-paper-deep transition-colors"
              style={{
                borderBottom: isActive
                  ? `3px solid ${cat.accent}`
                  : '3px solid transparent',
              }}
              aria-pressed={isActive}
            >
              <div
                className="font-label text-[10px] font-semibold uppercase tracking-[0.22em] mb-1"
                style={{ color: cat.accent }}
              >
                {cat.label}
              </div>
              <div className="font-editorial italic text-[12px] text-ink-muted leading-tight">
                {cat.tagline}
              </div>
              <div className="font-mono text-[11px] text-ink-soft mt-1.5 tabular-nums">
                {p.done}/{p.total} · {pct}%
              </div>
            </button>
          );
        })}
      </div>

      {/* Active tab colored banner */}
      <div
        className="border-l-4 px-5 py-4 mb-7"
        style={{ background: activeMeta.accentSoft, borderColor: activeMeta.accent }}
      >
        <div
          className="font-label text-[10px] font-semibold uppercase tracking-[0.22em] mb-1"
          style={{ color: activeMeta.accent }}
        >
          {activeMeta.label} · {activeMeta.tagline}
        </div>
        <p className="font-editorial text-[14.5px] text-ink-soft leading-relaxed max-w-3xl">
          {activeMeta.description}
        </p>
      </div>

      {/* Content */}
      {itemStatesQ.isLoading ? (
        <CenterText>Loading items…</CenterText>
      ) : activeCats.length === 0 ? (
        <div className="font-editorial italic text-ink-muted py-12 text-center">
          No items in the {activeMeta.label.toLowerCase()} tab match this selection.
        </div>
      ) : (
        activeCats.map((cat) => (
          <CategoryBlock
            key={cat.id}
            cat={cat}
            accent={activeMeta.accent}
            states={states}
            recentlySaved={recentlySaved}
            onToggle={doToggle}
          />
        ))
      )}
    </div>
  );
}

function CategoryBlock({
  cat,
  accent,
  states,
  recentlySaved,
  onToggle,
}: {
  cat: ChecklistCategory;
  accent: string;
  states: Record<string, ItemState>;
  recentlySaved: Record<string, number>;
  onToggle: (itemId: string, checked: boolean) => void;
}) {
  const total = countItems([cat]);
  const done = cat.sections.reduce(
    (sum, s) => sum + s.items.filter((it) => states[it.id]?.checked).length,
    0,
  );
  return (
    <section className="mb-12">
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-2">
        <h2 className="font-editorial text-[28px] font-medium leading-tight text-ink">
          <span className="italic font-normal mr-3 text-[18px]" style={{ color: accent }}>
            §{cat.number}
          </span>
          {cat.title}
        </h2>
        <div className="font-mono text-[11px] text-ink-muted tabular-nums">
          {done}/{total} done
        </div>
      </div>
      {cat.description && (
        <p className="font-editorial italic text-[14.5px] text-ink-muted mb-6 max-w-3xl leading-relaxed">
          {cat.description}
        </p>
      )}
      {cat.sections.map((sec) => (
        <div key={sec.id} className="mb-6">
          <h3 className="font-editorial text-[19px] font-medium text-ink mb-1 leading-snug">
            <span
              className="font-label text-[10px] font-medium uppercase tracking-[0.18em] mr-3 align-middle"
              style={{ color: accent }}
            >
              {sec.number}
            </span>
            {sec.title}
          </h3>
          {sec.description && (
            <p className="font-editorial italic text-[13.5px] text-ink-muted mb-3 max-w-3xl leading-relaxed">
              {sec.description}
            </p>
          )}
          <ul className="border-t border-dotted border-rule">
            {sec.items.map((it) => {
              const state = states[it.id];
              const checked = !!state?.checked;
              const justSaved = !!recentlySaved[it.id];
              return (
                <li
                  key={it.id}
                  className="border-b border-dotted border-rule"
                  style={{
                    background: checked ? '#f3ede0' : 'transparent',
                    transition: 'background-color 200ms ease',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => onToggle(it.id, !checked)}
                    className="w-full py-3.5 px-2 flex gap-4 items-start text-left hover:bg-paper-deep transition-colors group"
                    aria-pressed={checked}
                  >
                    {/* Checkbox — larger, thicker border, more obvious checked fill */}
                    <span
                      className="shrink-0 w-6 h-6 mt-0.5 border-2 inline-flex items-center justify-center"
                      style={{
                        borderColor: checked ? accent : '#807468',
                        background: checked ? accent : 'transparent',
                        transition:
                          'background-color 150ms ease, border-color 150ms ease, transform 120ms ease',
                        transform: checked ? 'scale(1.0)' : 'scale(1.0)',
                      }}
                      aria-hidden="true"
                    >
                      {checked && (
                        <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
                          <path
                            d="M3 8.5 L7 12 L13 4"
                            fill="none"
                            stroke="#fdfbf7"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </span>
                    <div className="flex-1">
                      <div
                        className="font-editorial text-[15.5px] leading-relaxed"
                        style={{
                          color: checked ? '#897a69' : 'var(--ink)',
                          textDecoration: checked ? 'line-through' : 'none',
                          textDecorationColor: checked ? accent : 'transparent',
                          textDecorationThickness: '1.5px',
                          transition: 'color 180ms ease',
                        }}
                      >
                        {it.text}
                      </div>
                      {it.note && (
                        <div
                          className="font-editorial italic text-[13px] mt-1"
                          style={{
                            color: checked ? '#b8ab9c' : accent,
                            textDecoration: checked ? 'line-through' : 'none',
                            textDecorationColor: '#b8ab9c',
                          }}
                        >
                          ↳ {it.note}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 pt-1 shrink-0">
                      <span className="font-label text-[10px] text-ink-muted tracking-wider whitespace-nowrap opacity-60 group-hover:opacity-100">
                        {it.id}
                      </span>
                      {justSaved && (
                        <span
                          className="font-label text-[10px] uppercase tracking-[0.18em] whitespace-nowrap"
                          style={{ color: accent }}
                        >
                          ✓ Saved
                        </span>
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </section>
  );
}

function Badge({ label }: { label: string }) {
  return (
    <span className="font-label text-[10px] font-medium uppercase tracking-[0.15em] text-ink-soft bg-paper border border-rule px-2.5 py-1">
      {label}
    </span>
  );
}

function CenterText({ children }: { children: React.ReactNode }) {
  return (
    <div className="max-w-md mx-auto px-6 py-24 text-center font-editorial italic text-ink-muted">
      {children}
    </div>
  );
}
